import { useState, useCallback, useEffect } from 'react';

/**
 * Gate one-time UI behind a localStorage flag.
 *
 * Returns `[isFirstVisit, dismiss]`. Calling `dismiss()` persists the flag so
 * the caller never returns `true` again for this key.
 *
 * Hydration-safe: the initial render always returns `false` (matching the
 * server pre-render), then a useEffect on mount reads localStorage and flips
 * to `true` if the key has not been seen. This avoids the React hydration
 * mismatch that occurs when the server renders nothing but the client
 * immediately renders a tour overlay.
 */
export function useFirstVisit(key: string): [boolean, () => void] {
  const [visible, setVisible] = useState(false);

  // Deferred read: only runs on the client, after hydration.
  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem(key) !== '1') {
        setVisible(true);
      }
    } catch { /* storage blocked */ }
  }, [key]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(key, '1');
    } catch { /* storage full or blocked */ }
  }, [key]);

  return [visible, dismiss];
}
